import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Wand2, Sparkles, Check, ChevronRight, Quote, ShieldCheck, Image as ImageIcon, ArrowRight } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { api } from "../lib/api";
import { toast } from "sonner";

const HERO_TRANSFORMATION = "https://customer-assets.emergentagent.com/job_pro-photo-membership/artifacts/cwvbpe21_Before%20and%20after%20photo.png";

const Header = ({ onCheckout }) => (
  <header className="sticky top-0 z-40 bg-[#FAFAF8]/90 backdrop-blur border-b border-[#e5e7eb]/60">
    <div className="container-bsp h-16 flex items-center justify-between">
      <a href="/" className="flex items-center gap-2" data-testid="logo-home">
        <div className="w-8 h-8 rounded-md bg-[#1A1F3D] flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-[#D4A853]" strokeWidth={1.6} />
        </div>
        <span className="font-display text-xl text-[#1A1F3D]">BrandSnapPro™</span>
      </a>
      <nav className="hidden md:flex items-center gap-8 text-sm">
        <a href="#how" className="text-[#52525b] hover:text-[#1A1F3D] transition-colors">How it works</a>
        <a href="#included" className="text-[#52525b] hover:text-[#1A1F3D] transition-colors">What's inside</a>
        <a href="#pricing" className="text-[#52525b] hover:text-[#1A1F3D] transition-colors">Pricing</a>
        <a href="#faq" className="text-[#52525b] hover:text-[#1A1F3D] transition-colors">FAQ</a>
        <a href="/login" className="text-[#1A1F3D] font-medium" data-testid="nav-login">Login</a>
      </nav>
      <button onClick={onCheckout} className="btn-primary text-sm" data-testid="nav-cta">
        Get Access — £79
      </button>
    </div>
  </header>
);

const Hero = ({ onCheckout, loading }) => (
  <section className="relative overflow-hidden">
    <div className="container-bsp pt-20 pb-24 md:pt-28 md:pb-32 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7 fade-up">
        <div className="flex items-center gap-3 mb-6">
          <span className="hairline" />
          <span className="eyebrow">A guided AI brand photo system</span>
        </div>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-[#1A1F3D] leading-[1.05] tracking-tight">
          Turn your selfies into <span className="italic">scroll-stopping</span> brand photos.
        </h1>
        <p className="mt-6 font-display text-2xl md:text-3xl text-[#1A1F3D]/85 leading-snug max-w-xl">
          Stop paying for photoshoots. Start creating brand photos from the ones you already have.
        </p>
        <p className="mt-6 text-lg text-[#52525b] max-w-xl leading-relaxed">
          A guided AI brand photo system that helps entrepreneurs and business owners create
          fresh, on-brand images of themselves — without another photoshoot.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <button onClick={onCheckout} disabled={loading} className="btn-primary" data-testid="hero-cta">
            {loading ? "Loading…" : "Get Instant Access — £79"}
            <ChevronRight className="w-4 h-4" strokeWidth={1.8} />
          </button>
          <a href="#how" className="btn-ghost text-center" data-testid="hero-secondary-cta">See how it works</a>
        </div>
        <div className="mt-10 flex items-center gap-6 text-sm text-[#52525b]">
          <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#D4A853]" /> One-time £79</div>
          <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#D4A853]" /> Yours forever</div>
          <div className="hidden sm:flex items-center gap-2"><Check className="w-4 h-4 text-[#D4A853]" /> No subscription</div>
        </div>
      </div>
      <div className="lg:col-span-5 relative" data-testid="hero-transformation">
        <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-[#F5E6E0] -z-0" />
        <div className="absolute -bottom-6 -right-6 w-40 h-40 rounded-full bg-[#D4A853]/30 -z-0" />
        <div className="relative">
          <div className="bsp-card overflow-hidden rounded-2xl">
            <img
              src={HERO_TRANSFORMATION}
              alt="Before selfie next to polished AI-generated brand photo"
              className="w-full h-auto block"
            />
          </div>
          <div className="bsp-card rounded-xl p-4 mt-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#1A1F3D] flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-[#D4A853]" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-[#1A1F3D]">Same person. New photo.</div>
              <div className="text-xs text-[#52525b]">From 6 reference selfies · generated in ~4 minutes</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Problem = () => (
  <section className="bg-[#F5E6E0]">
    <div className="container-bsp section">
      <div className="max-w-3xl">
        <span className="eyebrow">The problem</span>
        <h2 className="font-display text-4xl md:text-5xl text-[#1A1F3D] mt-4 leading-tight">
          You shouldn't have to book a photoshoot every time you need a new photo.
        </h2>
        <div className="mt-8 space-y-5 text-lg text-[#2D2D2D] leading-relaxed">
          <p>The same recycled headshot on every platform. The hours spent coordinating a shoot. The £800 invoice. The two-week wait.</p>
          <p>Then the moment you need a fresh image — for a launch, a podcast, a press feature — and you're stuck using one from 18 months ago.</p>
          <p className="font-display italic text-2xl md:text-3xl text-[#1A1F3D] pt-4">
            There has to be a better way. There is. And it still looks like you.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const WhatIsIt = () => (
  <section className="section">
    <div className="container-bsp grid md:grid-cols-12 gap-10 items-start">
      <div className="md:col-span-4">
        <span className="eyebrow">What is it?</span>
        <h2 className="font-display text-4xl md:text-5xl text-[#1A1F3D] mt-4">What is BrandSnapPro™?</h2>
      </div>
      <div className="md:col-span-7 md:col-start-6 text-lg text-[#2D2D2D] leading-relaxed space-y-5">
        <p>
          BrandSnapPro™ is a guided AI brand photo system. <span className="font-medium text-[#1A1F3D]">Not</span> a course you have to sit through. <span className="font-medium text-[#1A1F3D]">Not</span> another AI tool with random outputs. <span className="font-medium text-[#1A1F3D]">Not</span> a generic prompt pack.
        </p>
        <p>
          It's a complete system — prompts, prep guides, walkthrough video, troubleshooting library — that anyone can follow to get fresh, professional brand photos of themselves. Bring your face. We bring the rest.
        </p>
      </div>
    </div>
  </section>
);

const HowItWorks = () => {
  const steps = [
    { icon: Camera, title: "You bring a few real reference photos", body: "Five to ten clear photos of yourself. That's it. No studio. No outfit changes." },
    { icon: Wand2, title: "We walk you through the rest", body: "A guided system with prompts, prep guides, and a walkthrough video. No prompt-engineering required." },
    { icon: Sparkles, title: "New images of you. On-brand. Ready to use.", body: "Professional headshots, lifestyle shots, social tiles — all looking like you, ready to download." },
  ];
  return (
    <section id="how" className="bg-white border-y border-[#e5e7eb]">
      <div className="container-bsp section">
        <div className="max-w-2xl mb-16">
          <span className="eyebrow">How it works</span>
          <h2 className="font-display text-4xl md:text-5xl text-[#1A1F3D] mt-4">Three steps. That's the whole thing.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((s, i) => (
            <div key={i} className="bsp-card bsp-card-hover p-8 relative" data-testid={`how-step-${i + 1}`}>
              <div className="text-[#D4A853] font-display text-5xl absolute top-6 right-6 opacity-30">0{i + 1}</div>
              <div className="w-12 h-12 rounded-xl bg-[#1A1F3D] flex items-center justify-center mb-6">
                <s.icon className="w-6 h-6 text-[#D4A853]" strokeWidth={1.6} />
              </div>
              <h3 className="font-display text-2xl text-[#1A1F3D] leading-snug">{s.title}</h3>
              <p className="mt-3 text-[#52525b] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const BeforeAfter = () => {
  const REFERENCE_PHOTO = "https://customer-assets.emergentagent.com/job_pro-photo-membership/artifacts/uyae5van_Reference%20Photo.png";
  const cards = [
    {
      label: "Social Media",
      caption: "Square, vertical, banner — sized for every platform.",
      img: "https://customer-assets.emergentagent.com/job_pro-photo-membership/artifacts/6qt3hfo9_Social%20Media.png",
    },
    {
      label: "Office Setting",
      caption: "On-brand portraits at your desk — without scheduling a shoot.",
      img: "https://customer-assets.emergentagent.com/job_pro-photo-membership/artifacts/308u3of4_Office%20Setting.png",
    },
    {
      label: "City Setting",
      caption: "Editorial outdoor portraits with golden-hour light, on demand.",
      img: "https://customer-assets.emergentagent.com/job_pro-photo-membership/artifacts/wtd8gqxx_Professional%20Photo%20-%20City%20Setting.png",
    },
    {
      label: "Lifestyle",
      caption: "Warm, candid moments at your home office — just you and your work.",
      img: "https://customer-assets.emergentagent.com/job_pro-photo-membership/artifacts/r8d4sasr_Casual%20at%20Home%20Office.png",
    },
  ];
  return (
    <section className="section">
      <div className="container-bsp">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <span className="eyebrow">Before / After</span>
            <h2 className="font-display text-4xl md:text-5xl text-[#1A1F3D] mt-4">From this <span className="text-[#D4A853]">→</span> to this</h2>
          </div>
          <p className="text-[#52525b] max-w-md">Same person. Better photo. Generated end-to-end through the BrandSnapPro™ system.</p>
        </div>

        {/* Reference photo header */}
        <div className="mb-10 flex flex-col sm:flex-row items-center sm:items-end gap-5 sm:gap-7" data-testid="reference-photo-header">
          <div className="relative w-32 h-32 sm:w-36 sm:h-36 shrink-0 rounded-2xl overflow-hidden bg-[#F5E6E0] shadow-sm">
            <img
              src={REFERENCE_PHOTO}
              alt="Reference selfie used to generate every photo below"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <span className="absolute top-2 left-2 text-[9px] uppercase tracking-[0.18em] font-bold bg-white/90 text-[#1A1F3D] px-1.5 py-0.5 rounded">
              Before
            </span>
          </div>
          <div className="text-center sm:text-left">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4A853]">One reference selfie</div>
            <p className="font-display text-xl md:text-2xl text-[#1A1F3D] mt-1.5 leading-snug max-w-md">
              Started with this single phone selfie. Every brand photo below was generated from it.
            </p>
          </div>
          <div className="hidden lg:flex items-center gap-2 text-[#D4A853] ml-auto">
            <span className="h-px w-16 bg-[#D4A853]/40" />
            <ArrowRight className="w-5 h-5" strokeWidth={1.6} />
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1A1F3D]/70">After</span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map((c, i) => (
            <div key={i} className="bsp-card bsp-card-hover overflow-hidden flex flex-col" data-testid={`before-after-${i}`}>
              <div className="relative aspect-[4/5] bg-[#F5E6E0] overflow-hidden">
                <img
                  src={c.img}
                  alt={`${c.label} — BrandSnapPro after`}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <span className="absolute top-2 right-2 text-[9px] uppercase tracking-[0.18em] font-bold bg-[#D4A853] text-[#1A1F3D] px-1.5 py-0.5 rounded">
                  After
                </span>
              </div>
              <div className="p-5">
                <span className="text-xs uppercase tracking-wider font-semibold text-[#D4A853]">{c.label}</span>
                <p className="mt-2 text-sm text-[#2D2D2D] leading-relaxed">{c.caption}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Secondary example — male */}
        <div className="mt-10 max-w-3xl mx-auto grid sm:grid-cols-2 gap-6" data-testid="male-before-after">
          <div className="bsp-card bsp-card-hover overflow-hidden">
            <div className="relative aspect-[4/5] bg-[#F5E6E0] overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_pro-photo-membership/artifacts/k0aa7hwm_Reference%20Photo%20%282%29.png"
                alt="Male reference selfie — before"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <span className="absolute top-2 left-2 text-[9px] uppercase tracking-[0.18em] font-bold bg-white/90 text-[#1A1F3D] px-1.5 py-0.5 rounded">
                Before
              </span>
            </div>
          </div>
          <div className="bsp-card bsp-card-hover overflow-hidden">
            <div className="relative aspect-[4/5] bg-[#F5E6E0] overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_pro-photo-membership/artifacts/54cpizmn_Professional%20Headshot.png"
                alt="Professional headshot generated from the reference selfie"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <span className="absolute top-2 right-2 text-[9px] uppercase tracking-[0.18em] font-bold bg-[#D4A853] text-[#1A1F3D] px-1.5 py-0.5 rounded">
                After
              </span>
            </div>
          </div>
        </div>
        <p className="text-center text-sm text-[#52525b] mt-4 max-w-lg mx-auto">
          One selfie, one prompt — a polished professional headshot in minutes. Same system, any face.
        </p>
      </div>
    </section>
  );
};

const Included = () => {
  const items = [
    { title: "The Photo System", body: "Your guided AI photo workflow — step by step, designed to keep images looking like you." },
    { title: "Quick-Start Guide", body: "So you're not staring at a blank screen. Step one, step two, done." },
    { title: "Reference Photo Prep Guide", body: "The difference between results that look like you and results that look like a stranger starts here." },
    { title: "Walkthrough Video", body: "See the whole process in action before you upload a single photo." },
    { title: "Ready-to-Use Prompt Workflow", body: "No prompt engineering. No guessing. Fill-in-the-blank prompts that do the heavy lifting." },
    { title: "Member Resource Library", body: "14+ resources covering troubleshooting, cleanup tips, platform guidance, and file organisation." },
    { title: "Bonus: AI Bio Writer", body: "A ready-to-use prompt that drafts short, medium, and full-length professional bios in your brand voice — perfect for LinkedIn, About pages, and speaker intros." },
    { title: "Bonus: Virtual Try-On", body: "Drop in any outfit photo (a runway look, a shop product page, a Pinterest find) and see yourself wearing it — before you ever buy or book a stylist." },
  ];
  return (
    <section id="included" className="bg-[#1A1F3D] text-white">
      <div className="container-bsp section">
        <div className="max-w-2xl mb-14">
          <span className="eyebrow">Inside the membership</span>
          <h2 className="font-display text-4xl md:text-5xl text-white mt-4">What's inside BrandSnapPro™</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-7 hover:bg-white/10 transition-colors" data-testid={`feature-${i}`}>
              <div className="text-[#D4A853] font-display text-3xl">0{i + 1}</div>
              <h3 className="mt-3 font-display text-2xl text-white">{it.title}</h3>
              <p className="mt-3 text-white/70 leading-relaxed">{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ForYou = () => {
  const items = [
    "You're an entrepreneur, coach, business strategist, creator, or service provider",
    "You need to look polished and credible online",
    "You've tried AI image tools before and results looked nothing like you",
    "You want more photos without starting from scratch every time",
    "You'd rather spend time running your business than coordinating shoots",
    "Your image matters to your revenue",
  ];
  return (
    <section className="section">
      <div className="container-bsp grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-5">
          <span className="eyebrow">Is this you?</span>
          <h2 className="font-display text-4xl md:text-5xl text-[#1A1F3D] mt-4 leading-tight">This is for you if…</h2>
        </div>
        <ul className="md:col-span-7 space-y-4">
          {items.map((t, i) => (
            <li key={i} className="flex items-start gap-4 bsp-card p-5" data-testid={`for-you-${i}`}>
              <span className="mt-1 w-7 h-7 rounded-full bg-[#D4A853]/20 flex items-center justify-center shrink-0">
                <Check className="w-4 h-4 text-[#1A1F3D]" strokeWidth={2.4} />
              </span>
              <span className="text-[#2D2D2D] text-lg leading-snug">{t}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

const Pricing = ({ onCheckout, loading }) => {
  const includes = [
    "The full Photo System workflow",
    "Quick-Start Guide + Walkthrough Video",
    "Reference Photo Prep Guide",
    "Ready-to-Use Prompt Workflow",
    "Member Resource Library (14+)",
    "Bonus: AI Bio Writer + Virtual Try-On prompts",
    "Personal one-on-one help if results aren't there",
  ];
  return (
    <section id="pricing" className="bg-[#FAFAF8]">
      <div className="container-bsp section">
        <div className="max-w-3xl mx-auto bsp-card p-10 md:p-14 text-center" data-testid="pricing-card">
          <span className="eyebrow">Get instant access</span>
          <h2 className="font-display text-4xl md:text-5xl text-[#1A1F3D] mt-4">Get instant access to BrandSnapPro™</h2>
          <div className="mt-8 flex items-baseline justify-center gap-2">
            <span className="font-display text-7xl md:text-8xl text-[#1A1F3D]">£79</span>
            <span className="text-[#52525b] text-lg line-through ml-2">£500–£1500</span>
          </div>
          <p className="mt-2 text-[#52525b]">One-time payment. Yours forever. No subscriptions.</p>
          <ul className="mt-8 grid sm:grid-cols-2 gap-3 text-left">
            {includes.map((t, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#D4A853] mt-1 shrink-0" />
                <span className="text-[#2D2D2D]">{t}</span>
              </li>
            ))}
          </ul>
          <button onClick={onCheckout} disabled={loading} className="btn-primary mt-10" data-testid="pricing-cta">
            {loading ? "Redirecting…" : "Get Instant Access — £79"}
            <ChevronRight className="w-4 h-4" />
          </button>
          <p className="mt-6 text-sm text-[#52525b]">A professional brand photoshoot costs £500–£1,500. BrandSnapPro™ is £79. Once.</p>
        </div>
      </div>
    </section>
  );
};

const Guarantee = () => (
  <section className="section">
    <div className="container-bsp">
      <div className="max-w-3xl mx-auto bsp-card p-10 md:p-14 border-t-4 border-[#D4A853]">
        <div className="flex items-start gap-5 sm:gap-6">
          <img
            src="https://customer-assets.emergentagent.com/job_pro-photo-membership/artifacts/qgowlt3o_ChatGPT%20Image%20Apr%2030%2C%202026%2C%2006_23_01%20PM.png"
            alt="Founder portrait"
            className="hidden sm:block w-24 h-24 md:w-28 md:h-28 rounded-full object-cover ring-2 ring-[#D4A853]/40 shadow-sm shrink-0"
            loading="lazy"
            data-testid="guarantee-portrait"
          />
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-6 h-6 text-[#D4A853]" strokeWidth={1.6} />
              <span className="eyebrow">My personal guarantee</span>
            </div>
            <div className="flex items-center gap-4 mt-4 sm:hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_pro-photo-membership/artifacts/qgowlt3o_ChatGPT%20Image%20Apr%2030%2C%202026%2C%2006_23_01%20PM.png"
                alt="Founder portrait"
                className="w-16 h-16 rounded-full object-cover ring-2 ring-[#D4A853]/40 shadow-sm"
                loading="lazy"
              />
              <h2 className="font-display text-3xl text-[#1A1F3D]">My Personal Guarantee</h2>
            </div>
            <h2 className="hidden sm:block font-display text-3xl md:text-4xl text-[#1A1F3D] mt-3">My Personal Guarantee</h2>
            <p className="mt-5 text-lg text-[#2D2D2D] leading-relaxed">
              I want you to walk away with photos you're genuinely excited to use. If you go through the
              process and your results aren't there yet, reach out directly and I'll work with you
              one-on-one until they are. <span className="font-medium text-[#1A1F3D]">No runaround. No fine print.</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const FAQ = () => {
  const faqs = [
    { q: "Will it actually look like me?", a: "Yes — when you follow the Reference Photo Prep Guide and use the system properly, results stay faithful to your real face. The whole point is photos that still look like you." },
    { q: "Do I need a ChatGPT Plus subscription to use this?", a: "No — you can access BrandSnapPro™ with a free ChatGPT account." },
    { q: "How long does it take to get my first images?", a: "Most members generate their first usable images within 30–60 minutes of joining." },
    { q: "I'm not very techy. Can I do this?", a: "Absolutely. The walkthrough video and quick-start guide are designed for non-technical users. If you can copy and paste, you can do this." },
    { q: "Will my photos look AI-generated?", a: "No — the prompt workflow includes specific lighting and lens language that makes outputs look photographed, not rendered." },
    { q: "What if my results don't look like me?", a: "Use the troubleshooting library, then reach out to me directly. I'll work with you one-on-one until they do." },
    { q: "What can I use these photos for?", a: "Anything: website, LinkedIn, Instagram, press features, podcasts, ads, email signatures. They're your photos to use commercially." },
  ];
  return (
    <section id="faq" className="bg-white border-y border-[#e5e7eb]">
      <div className="container-bsp section">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <span className="eyebrow">FAQ</span>
            <h2 className="font-display text-4xl md:text-5xl text-[#1A1F3D] mt-4 leading-tight">Questions, answered.</h2>
            <p className="mt-4 text-[#52525b]">Still curious? Email us — we read every message.</p>
          </div>
          <div className="md:col-span-8" data-testid="faq-accordion">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#e5e7eb]">
                  <AccordionTrigger className="text-lg font-medium text-[#1A1F3D] py-6 hover:no-underline" data-testid={`faq-q-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#2D2D2D] text-base leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

const FinalCTA = ({ onCheckout, loading }) => {
  const testimonials = [
    { name: "Anya M.", role: "Brand Strategist", text: "I haven't booked a shoot in 6 months. Every launch I just open BrandSnapPro and have new photos in an hour." },
    { name: "Daniel R.", role: "Business Strategist", text: "Worth it for the prep guide alone. My results finally look like me." },
    { name: "Priya S.", role: "Coach", text: "The prompt workflow is the part nobody else gets right. This is the missing piece." },
  ];
  return (
    <section className="bg-[#1A1F3D] text-white">
      <div className="container-bsp section">
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-7" data-testid={`testimonial-${i}`}>
              <Quote className="w-6 h-6 text-[#D4A853]" />
              <p className="mt-4 text-white/90 leading-relaxed">"{t.text}"</p>
              <div className="mt-5 text-sm">
                <div className="font-semibold text-white">{t.name}</div>
                <div className="text-white/60">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="font-display text-4xl md:text-5xl">Stop waiting on your next photoshoot.</h2>
          <p className="mt-5 text-white/70 text-lg">Get the system. Get the photos. Get back to work.</p>
          <button onClick={onCheckout} disabled={loading} className="btn-primary mt-10" data-testid="final-cta">
            {loading ? "Redirecting…" : "Get BrandSnapPro™ for £79"}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="bg-[#FAFAF8] border-t border-[#e5e7eb]">
    <div className="container-bsp py-10 flex flex-col gap-4 text-sm text-[#52525b]">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#D4A853]" />
          <span>© 2026 BrandSnap.pro</span>
        </div>
        <div className="flex items-center gap-6 flex-wrap">
          <a href="/login" className="hover:text-[#1A1F3D]" data-testid="footer-login">Login</a>
          <a href="#faq" className="hover:text-[#1A1F3D]">FAQ</a>
          <a href="https://docs.google.com/document/d/151ibK7XPyU7Oo4KTfmUrwj3_Z0WxQ7NI/edit?usp=sharing&ouid=118030254887284270003&rtpof=true&sd=true" target="_blank" rel="noopener noreferrer" className="hover:text-[#1A1F3D]">Privacy Policy</a>
          <a href="https://docs.google.com/document/d/1DAZhD5SxETWHd_diJoD3heMq3qIi-OnP/edit?usp=sharing&ouid=118030254887284270003&rtpof=true&sd=true" target="_blank" rel="noopener noreferrer" className="hover:text-[#1A1F3D]">Terms of Use</a>
          <a href="mailto:contact@aicompass.co.uk" className="hover:text-[#1A1F3D]">contact@aicompass.co.uk</a>
        </div>
      </div>
    </div>
  </footer>
);

export default function LandingPage() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const startCheckout = async () => {
    try {
      setLoading(true);
      const r = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#FAFAF8] text-[#2D2D2D]">
      <Header onCheckout={startCheckout} />
      <Hero onCheckout={startCheckout} loading={loading} />
      <Problem />
      <WhatIsIt />
      <HowItWorks />
      <BeforeAfter />
      <Included />
      <ForYou />
      <Pricing onCheckout={startCheckout} loading={loading} />
      <Guarantee />
      <FAQ />
      <FinalCTA onCheckout={startCheckout} loading={loading} />
      <Footer />
    </div>
  );
}
